import type { ColumnDef } from '@tanstack/react-table'
import type { ITicketConfig } from '@/shared/api/types/ticket-config-types'
import type { PaginationConfig } from '@/shared/ui/data-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveDashboardLocalizedValue } from '@/shared/utils/dashboard-localized-value'
import { DataTable } from '@/shared/ui/data-table'
import { TicketConfigStatusToggle } from '@/features/ticket-config/ticket-config-status-toggle'

interface TicketConfigListTableProps {
  data: ITicketConfig[]
  loading: boolean
  pagination: PaginationConfig
  tenantId?: string
  languageCode: string
  onEdit: (config: ITicketConfig) => void
}

export function TicketConfigListTable({
  data,
  loading,
  pagination,
  tenantId,
  languageCode,
  onEdit,
}: TicketConfigListTableProps) {
  const { t } = useTranslation()

  const columns = useMemo<ColumnDef<ITicketConfig>[]>(
    () => [
      {
        id: 'name',
        header: t('common.name'),
        cell: ({ row }) => {
          const config = row.original
          const localized = config.localizedData
            ? resolveDashboardLocalizedValue(config.localizedData, languageCode)
            : config.id
          return <span className="font-medium">{localized}</span>
        },
      },
      {
        id: 'ticketType',
        size: 112,
        header: () => (
          <div className="text-center">
            {t('ticketConfig.ticketType', { defaultValue: 'Type' })}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex justify-center">
            <span className="min-w-[72px] text-center text-sm font-medium text-zinc-700">
              {row.original.ticketType === 'paid'
                ? t('ticketConfig.paid', { defaultValue: 'Paid' })
                : t('ticketConfig.free', { defaultValue: 'Free' })}
            </span>
          </div>
        ),
      },
      {
        id: 'status',
        header: () => <div className="text-center">{t('common.status')}</div>,
        cell: ({ row }) => (
          <TicketConfigStatusToggle
            configId={row.original.id}
            {...(tenantId ? { tenantId } : {})}
            isActive={row.original.isActive}
          />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: t('common.createdAt'),
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
      },
    ],
    [languageCode, onEdit, t, tenantId],
  )

  return (
    <DataTable
      columns={columns}
      data={data}
      loading={loading}
      pagination={pagination}
      onRowClick={onEdit}
    />
  )
}
